/**
 * ECOMM-13966 Phase 2: Kiosk Pro touch diagnostics.
 *
 * Investigates touch-routing / hit-test offset on Elo Backpack 4 / Edge kiosks.
 *
 * Three independent safety gates:
 * 1. LaunchDarkly flag: `ecomm.kiosk-pro-touch-diagnostics.operational` (JSON, must have `enabled === true`).
 * 2. Code allowlist: in production, only MPX Sandbox (`65c10de35125cf000935f594`) is allowed.
 * 3. Mount point: mounted strictly inside KioskProLayout (never on regular ecomm or Kiosk Basic).
 *
 * When any gate is closed, this component returns null and attaches zero listeners/DOM.
 * Temporary diagnostic code for ECOMM-13966 investigation; remove after root cause resolution.
 */

import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import PublicEnv from 'shared/utils/public-env';
import type { Dispensaries } from 'src/gql/graphql';
import { TOUCH_DIAGNOSTICS_FLAG_KEY } from './constants';
import { TouchDiagnosticsOverlay } from './touch-diagnostics-overlay';
import { useTouchDiagnostics } from './use-touch-diagnostics';
import { isDiagnosticsAllowed, parseDiagnosticsFlag } from './utils';

export type KioskProTouchDiagnosticsProps = {
  dispensary?: Dispensaries | null;
};

export const KioskProTouchDiagnostics = ({
  dispensary,
}: KioskProTouchDiagnosticsProps): JSX.Element | null => {
  const flags = useFlags();
  const rawFlagValue = flags[TOUCH_DIAGNOSTICS_FLAG_KEY];
  const config = parseDiagnosticsFlag(rawFlagValue);

  const isAllowed = isDiagnosticsAllowed({
    dispensaryId: dispensary?.id,
    appEnv: PublicEnv.appEnv,
  });

  const shouldRun = config.enabled && isAllowed;

  // We only run the hook when enabled and allowed; when disabled, the hook receives enabled=false
  // and attaches zero listeners.
  const activeConfig = shouldRun
    ? config
    : { enabled: false, overlay: false, hitMapIntervalMs: 60000 };

  const { lastEvent, barRect, hudState } = useTouchDiagnostics({ config: activeConfig });

  if (!shouldRun) {
    return null;
  }

  return (
    <TouchDiagnosticsOverlay
      overlay={config.overlay}
      lastEvent={lastEvent}
      barRect={barRect}
      hudState={hudState}
    />
  );
};
